/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { useI18n } from "hooks/i18n/i18n";
import { EVENT_DATE_TYPE } from "pages/AccountSettlementHistoryOrderId/constants/EventDateType";
import { PRODUCT_TYPE } from "pages/AccountSettlementHistoryOrderId/constants/ProductType";
import {
  infoDetailTextStyle,
  infoOptionalTextStyle,
  infoTitleTextStyle,
  infoWrapperStyle,
} from "pages/AccountSettlementHistoryOrderId/styles/commonStyles";
import React from "react";
import { StatusLabel } from "ui/StatusLabel";

import {
  goodsCardStyle,
  liveImageContainerStyle,
  liveImageSkeletonStyle,
  liveImageStyle,
  rootStyle,
  thumbnailImageStyle,
  thumbnailSkeletonStyle,
  liveCardStyle,
  statusBarStyle,
  infoTextStyle,
  infoTextItemStyle,
} from "./styles";

import type { EventDateInfo } from "pages/AccountSettlementHistoryOrderId/types/EventDateInfo";
import type { FC } from "react";
import type { EVENT_STATUS } from "ui/StatusLabel/constants/eventStatus";

type ProductType = (typeof PRODUCT_TYPE)[keyof typeof PRODUCT_TYPE];

type LiveTicketInfo = {
  dateInfo: EventDateInfo;
  status: (typeof EVENT_STATUS)[keyof typeof EVENT_STATUS];
  eventId: string;
};

type Props = {
  type: ProductType;
  imageSrc: string;
  isImageLoading: boolean;
  productName: string;
  price: number;
  quantity: number;
  variationName: string;
  liveTicketInfo?: LiveTicketInfo;
};

type ProductDetailsProps = {
  productName: string;
  price: number;
  quantity: number;
  variationName: string;
  children?: React.ReactNode;
};

const StatusBar: FC<{ liveTicketInfo: LiveTicketInfo }> = ({
  liveTicketInfo,
}) => {
  const { dateInfo } = liveTicketInfo;

  return (
    <div css={statusBarStyle}>
      <p css={infoTextStyle}>
        {dateInfo.type === EVENT_DATE_TYPE.SINGLE_DAY && (
          <React.Fragment>
            <span css={infoTextItemStyle}>{dateInfo.openDate}</span>
            <span css={infoTextItemStyle}>OPEN {dateInfo.openTime}</span>
          </React.Fragment>
        )}
        {dateInfo.type === EVENT_DATE_TYPE.RANGE_DAYS && (
          <React.Fragment>
            <span css={infoTextItemStyle}>{dateInfo.openDate}</span>
            <span css={infoTextItemStyle}>〜 {dateInfo.closeDate}</span>
          </React.Fragment>
        )}
      </p>
      <StatusLabel status={liveTicketInfo.status} />
    </div>
  );
};

const ProductDetails: FC<ProductDetailsProps> = ({
  productName,
  price,
  quantity,
  variationName,
  children,
}) => {
  const { t } = useI18n();
  return (
    <div css={infoWrapperStyle}>
      {children}
      <h4 css={infoTitleTextStyle}>{variationName}</h4>
      <p css={infoDetailTextStyle}>
        <span>¥{price.toLocaleString("ja-JP")}/</span>
        <span>
          {t("settlementHistory.productDetails.quantity", { quantity })}
        </span>
      </p>
      <p css={infoOptionalTextStyle}>{productName}</p>
    </div>
  );
};

export const ProductInfo: FC<Props> = ({
  type,
  imageSrc,
  isImageLoading,
  productName,
  price,
  quantity,
  variationName,
  liveTicketInfo,
}) => {
  const isLiveTicket = type === PRODUCT_TYPE.TICKET;

  return (
    <div css={rootStyle}>
      {isLiveTicket && (
        <div css={liveImageContainerStyle}>
          {isImageLoading ? (
            <div css={liveImageSkeletonStyle} />
          ) : (
            <img
              css={liveImageStyle}
              src={imageSrc}
              alt={productName}
              width="440"
              height="185"
            />
          )}
        </div>
      )}
      <div css={isLiveTicket ? liveCardStyle : goodsCardStyle}>
        {!isLiveTicket &&
          (isImageLoading ? (
            <div css={thumbnailSkeletonStyle} />
          ) : (
            <img
              css={thumbnailImageStyle}
              src={imageSrc}
              alt={productName}
              width="80"
              height="80"
            />
          ))}
        <ProductDetails
          productName={productName}
          price={price}
          quantity={quantity}
          variationName={variationName}
        >
          {liveTicketInfo &&
            liveTicketInfo.dateInfo.type !== EVENT_DATE_TYPE.NO_DATE && (
              <StatusBar liveTicketInfo={liveTicketInfo} />
            )}
        </ProductDetails>
      </div>
    </div>
  );
};
